// Login and password-recovery APIs used before the user enters a role dashboard.
import axiosApi, { clearCsrfToken, getSessionJson, postJson } from "./axiosApi";

// Keep axiosApi imported here so this file always uses the centralized HTTP client layer.
void axiosApi;

export async function getCurrentSession() {
  const { response, data } = await getSessionJson(
    "/dashboard/session.php",
    10000,
    true,
  );

  if (response.status === 401) {
    clearCsrfToken();
    return null;
  }
  if (
    response.status < 200 ||
    response.status >= 300 ||
    data.success === false
  ) {
    throw new Error(data.message || "Session check failed.");
  }
  return data;
}

export function clearStoredSession() {
  clearCsrfToken();
}

async function refreshLoginCsrfToken() {
  const { response, data } = await getSessionJson("/dashboard/session.php", 10000, true);
  if (response.status < 200 || response.status >= 300 || data.success === false || !data.csrf_token) {
    throw new Error("Could not prepare a secure login session. Please try again.");
  }
  return data.csrf_token;
}

export async function loginWithPassword(identifier, password, location = {}, forceLogin = false, retried = false, dbRetryCount = 0) {
  let csrfToken = window.sessionStorage.getItem("kaamdesk_csrf_token") || "";
  if (!csrfToken) csrfToken = await refreshLoginCsrfToken();

  try {
    return await postJson(
      "/login/password_login.php",
      { identifier, password, ...location, force_login: forceLogin, csrf_token: csrfToken },
      { includeErrorDetail: true, attachValidationErrors: true },
    );
  } catch (error) {
    // If MySQL briefly reaches the hosting account's connection ceiling, do not
    // make the user resubmit credentials. The backend returns a retryable code;
    // retry only twice with jitter so concurrent browsers do not retry together.
    if (error?.code === "DB_CONNECTION_LIMIT" && dbRetryCount < 2) {
      const delay = 700 + Math.floor(Math.random() * 900) + dbRetryCount * 700;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      return loginWithPassword(identifier, password, location, forceLogin, retried, dbRetryCount + 1);
    }

    // A login page can remain open while its guest PHP session is regenerated.
    // Refresh the token once transparently instead of asking the user to reload.
    if (!retried && /security token expired/i.test(String(error?.message || ""))) {
      await refreshLoginCsrfToken();
      return loginWithPassword(identifier, password, location, forceLogin, true, dbRetryCount);
    }
    throw error;
  }
}

export function sendPasswordResetOtp(identifier) {
  return postJson(
    "/login/send_password_reset_otp.php",
    { identifier },
    { includeErrorDetail: true, attachValidationErrors: true },
  );
}

export function verifyPasswordResetOtp(otp) {
  return postJson(
    "/login/verify_password_reset_otp.php",
    { otp },
    { includeErrorDetail: true, attachValidationErrors: true },
  );
}

export function resetPassword(password) {
  return postJson(
    "/login/reset_password.php",
    { password },
    { includeErrorDetail: true, attachValidationErrors: true },
  );
}
